import React, { useEffect, useState } from "react"
import SSESubscriberService from "./SSESubscriberService"
import { logCheckoutConfirmed } from "../context/analytics-handlers"
import { translationKeys } from "../util/translationKeys"
import { useTranslation } from "react-i18next"
import { useI18next } from "gatsby-plugin-react-i18next"
import { CheckoutOrder } from "../models/order"
import { trackGTMPurchase } from "./gtm-datalayer-handlers"
import { useAPI } from "./api"
import { captureException } from "./exception-logger"

const SSE_EVENT_TYPES = {
  ORDER_PAID: "order_paid",
  ORDER_FAILED: "order_payment_canceled",
}

type OrderStatusEventType = {
  id: string
  type: string
}

export const useOrderStatusSSE = (
  timeoutId: React.MutableRefObject<NodeJS.Timeout | undefined>,
  order: CheckoutOrder,
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void,
  showPaymentSucceededScreen: () => void,
  setTimeoutId: (val: NodeJS.Timeout | undefined) => void,
  setIsLoading: (val: boolean) => void
) => {
  const { t } = useTranslation()
  const { language } = useI18next()
  const [sseSubscriber, setSseSubscriber] = useState<SSESubscriberService>()
  const api = useAPI()

  useEffect(() => {
    if (!order) {
      return
    }
    const sseSubscriber = new SSESubscriberService(
      order.order.sse_jwt,
      order.order._links.sse_subscribe
    )
    setSseSubscriber(sseSubscriber)
  }, [order])

  const subscribe = () => {
    return useEffect(() => {
      if (!sseSubscriber) {
        return
      }

      const onMessageReceived = async (e: any) => {
        const parsedData = JSON.parse(e.data) as OrderStatusEventType

        if (parsedData.type === SSE_EVENT_TYPES.ORDER_PAID) {
          try {
            const updatedOrder = await api.checkout.retrieveOrderById(
              order.order.id,
              language
            )
            if (updatedOrder) {
              logCheckoutConfirmed(updatedOrder.order)
              trackGTMPurchase(updatedOrder.order)
            }
          } catch (error) {
            // The user has paid at this point, so this must not block the
            // success screen - but a failure here means our order data or
            // purchase tracking is off
            captureException(error, {
              type: "business_logic",
              category: "invalid-state",
              context: {
                errorSource: "useOrderStatusSSE - order paid follow-up",
                orderId: order.order.id,
              },
            })
          }
          showPaymentSucceededScreen()
        } else if (parsedData.type === SSE_EVENT_TYPES.ORDER_FAILED) {
          // A canceled payment is an expected outcome, not an error; the
          // failed screen reports it as the payment_failed_screen_shown action
          showPaymentFailedScreen(`${t(translationKeys.webPaymentFailed)}`)
        } else {
          captureException(
            new Error(`Unknown SSE event type: ${parsedData.type}`),
            {
              type: "business_logic",
              category: "invalid-state",
              context: {
                errorSource: "useOrderStatusSSE",
                eventType: parsedData.type,
                orderId: order.order.id,
              },
            }
          )
          return
        }
        sseSubscriber.close()
        setIsLoading(false)
        if (timeoutId.current) {
          clearTimeout(timeoutId.current)
          setTimeoutId(undefined)
        }
      }

      sseSubscriber.addEventListener("message", onMessageReceived)
      return () => {
        sseSubscriber.removeEventListener("message", onMessageReceived)
        sseSubscriber.close()
      }
    }, [sseSubscriber])
  }

  return { subscribe, sseSubscriber }
}
